import React from 'react';
import { TestScenario } from '../SecretTestRunner';

interface TestScenarioSelectorProps {
  scenarios: TestScenario[];
  selectedScenarios: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  isExecuting: boolean;
}

const categoryColors = {
  auth: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
  workflow: 'border-green-200 bg-green-50 text-green-700 dark:border-green-700 dark:bg-green-900/20 dark:text-green-300',
  platform: 'border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-700 dark:bg-purple-900/20 dark:text-purple-300',
  performance: 'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-700 dark:bg-orange-900/20 dark:text-orange-300',
  accessibility: 'border-pink-200 bg-pink-50 text-pink-700 dark:border-pink-700 dark:bg-pink-900/20 dark:text-pink-300'
};

const categoryIcons = {
  auth: '🔐',
  workflow: '⚡',
  platform: '🔗',
  performance: '🚀',
  accessibility: '♿'
};

export const TestScenarioSelector: React.FC<TestScenarioSelectorProps> = ({
  scenarios,
  selectedScenarios,
  onSelectionChange,
  isExecuting
}) => {
  const handleScenarioToggle = (scenarioId: string) => {
    if (isExecuting) return;
    
    if (selectedScenarios.includes(scenarioId)) {
      onSelectionChange(selectedScenarios.filter(id => id !== scenarioId));
    } else {
      onSelectionChange([...selectedScenarios, scenarioId]);
    }
  };

  const handleSelectAll = () => {
    if (isExecuting) return;
    
    if (selectedScenarios.length === scenarios.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(scenarios.map(s => s.id));
    }
  };

  const handleSelectByCategory = (category: TestScenario['category']) => {
    if (isExecuting) return;
    
    const categoryScenarios = scenarios.filter(s => s.category === category);
    const categoryIds = categoryScenarios.map(s => s.id);
    const allCategorySelected = categoryIds.every(id => selectedScenarios.includes(id));
    
    if (allCategorySelected) {
      // Deselect all in category
      onSelectionChange(selectedScenarios.filter(id => !categoryIds.includes(id)));
    } else {
      // Select all in category
      const newSelection = [...new Set([...selectedScenarios, ...categoryIds])];
      onSelectionChange(newSelection);
    }
  };

  const groupedScenarios = scenarios.reduce((acc, scenario) => {
    if (!acc[scenario.category]) {
      acc[scenario.category] = [];
    }
    acc[scenario.category].push(scenario);
    return acc;
  }, {} as Record<TestScenario['category'], TestScenario[]>);

  return (
    <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg border border-gray-200 dark:border-gray-600 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Test Scenarios
        </h2>
        <button
          onClick={handleSelectAll}
          disabled={isExecuting}
          className="px-3 py-1 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {selectedScenarios.length === scenarios.length ? 'Deselect All' : 'Select All'}
        </button>
      </div>

      <div className="space-y-6">
        {Object.entries(groupedScenarios).map(([category, categoryScenarios]) => (
          <div key={category} className="space-y-3">
            {/* Category Header */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => handleSelectByCategory(category as TestScenario['category'])}
                disabled={isExecuting}
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${categoryColors[category as TestScenario['category']]} hover:opacity-80 disabled:cursor-not-allowed`}
              >
                <span className="mr-1">
                  {categoryIcons[category as TestScenario['category']]}
                </span>
                {category.charAt(0).toUpperCase() + category.slice(1)}
                <span className="ml-1 text-xs opacity-75">
                  ({categoryScenarios.length})
                </span>
              </button>
            </div>

            {/* Scenarios in Category */}
            <div className="space-y-2">
              {categoryScenarios.map((scenario) => (
                <div
                  key={scenario.id}
                  className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                    selectedScenarios.includes(scenario.id)
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  } ${isExecuting ? 'cursor-not-allowed opacity-60' : ''}`}
                  onClick={() => handleScenarioToggle(scenario.id)}
                >
                  <div className="flex items-center space-x-3">
                    {/* Checkbox */}
                    <div className="flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={selectedScenarios.includes(scenario.id)}
                        onChange={() => handleScenarioToggle(scenario.id)}
                        disabled={isExecuting}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:cursor-not-allowed"
                      />
                    </div>

                    {/* Scenario Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate pr-2">
                          {scenario.name}
                        </h3>
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                          {scenario.duration}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
                        {scenario.description}
                      </p>
                      <code className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded block truncate">
                        {scenario.command.split(' ').slice(-1)[0]}
                      </code>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Selection Summary */}
      {selectedScenarios.length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">
              Selected: {selectedScenarios.length} test{selectedScenarios.length !== 1 ? 's' : ''}
            </span>
            <span className="text-gray-500 dark:text-gray-500">
              Est. duration: ~{Math.ceil(selectedScenarios.length * 3)} min
            </span>
          </div>
        </div>
      )}
    </div>
  );
};